/*
 * File: ExecutionHistory.js
 * Version: v1.0.0
 * Last Modified: 2025-01-27
 * Project: Asterism User GUI
 * Description: Component for displaying execution history of repetitive observations
 * Dependencies: React, Material-UI, date-fns, recharts
 * Change Log:
 *   v1.0.0 - Initial implementation for repetitive observation support
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Card,
  CardContent,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
  Close as CancelIcon,
  Schedule as ScheduledIcon,
  SkipNext as SkippedIcon,
  Refresh as RefreshIcon,
  PlayArrow as ResumeIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiService } from '../services/apiService';

const ExecutionHistory = ({ observationId, isOpen = false, onClose = null }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Helper to format dates in UTC with 24-hour time
  const formatUTC = (dateStr) => {
    if (!dateStr) return '-';
    try {
      // Format: Oct 3, 2025, 14:24:43 UTC (24-hour format)
      return formatInTimeZone(parseISO(dateStr), 'UTC', 'PPP, HH:mm:ss') + ' UTC';
    } catch (e) {
      return '-';
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (observationId) {
      loadExecutionHistory();
    }
  }, [observationId]);

  const loadExecutionHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getExecutionHistory(observationId);
      setData(response);
    } catch (err) {
      console.error('Failed to load execution history:', err);
      setError('Failed to load execution history');
    } finally {
      setLoading(false);
    }
  };

  const handlePauseResume = async () => {
    try {
      if (data.series_stats?.status === 'paused') {
        await apiService.resumeRepetitiveObservation(observationId);
      } else {
        await apiService.pauseRepetitiveObservation(observationId);
      }
      loadExecutionHistory(); // Reload data
    } catch (err) {
      console.error('Failed to pause/resume observation:', err);
      setError('Failed to pause/resume observation');
    }
  };

  const getExecutionStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'failed':
      case 'aborted':
        return <FailedIcon color="error" />;
      case 'skipped':
        return <SkippedIcon color="warning" />;
      default:
        return <ScheduledIcon color="action" />;
    }
  };

  const getExecutionStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
      case 'aborted':
        return 'error';
      case 'skipped':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Prepare data for charts
  const prepareChartData = () => {
    if (!data?.execution_history) return { pieData: [], lineData: [] };

    const stats = data.series_stats || {};
    const pieData = [
      { name: 'Completed', value: stats.total_completed || 0, color: '#4caf50' },
      { name: 'Failed', value: stats.total_failed || 0, color: '#f44336' },
      { name: 'Skipped', value: stats.total_skipped || 0, color: '#ff9800' },
    ].filter(item => item.value > 0);

    // Line chart data: success rate over time
    const lineData = data.execution_history
      .filter(exec => exec.status !== 'skipped')
      .map((exec, index, arr) => {
        const previousExecutions = arr.slice(0, index + 1);
        const completed = previousExecutions.filter(e => e.status === 'completed').length;
        const total = previousExecutions.length;
        const successRate = total > 0 ? (completed / total) * 100 : 0;

        const dateStr = exec.completed_at || exec.started_at;
        return {
          iteration: exec.iteration,
          date: dateStr ? format(parseISO(dateStr), 'MMM dd') : 'N/A',
          successRate: Math.round(successRate)
        };
      });

    return { pieData, lineData };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No execution history available
      </Alert>
    );
  }

  const { pieData, lineData } = prepareChartData();
  const isRepetitive = data.repetition_config?.enabled;
  const canPause = isRepetitive && ['waiting_next', 'paused'].includes(data.status);

  const content = (
    <Box>
      {/* Header with stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Executions
              </Typography>
              <Typography variant="h4">
                {data.series_stats?.total_completed || 0} / {data.series_stats?.total_planned || '∞'}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  data.series_stats?.total_planned
                    ? (data.series_stats.total_completed / data.series_stats.total_planned) * 100
                    : 0
                }
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h4" color={data.series_stats?.success_rate >= 80 ? 'success.main' : 'warning.main'}>
                {Math.round(data.series_stats?.success_rate || 0)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {data.series_stats?.total_completed || 0} completed, {data.series_stats?.total_failed || 0} failed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Next Execution
              </Typography>
              {data.next_execution?.ideal_time ? (
                <>
                  <Typography variant="h6">
                    {formatDistanceToNow(parseISO(data.next_execution.ideal_time), { addSuffix: true })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatUTC(data.next_execution.ideal_time)}
                  </Typography>
                </>
              ) : (
                <Typography variant="h6" color="textSecondary">
                  {data.status === 'completed_series' ? 'Series Complete' : 'Not Scheduled'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Repetition Config
              </Typography>
              {data.repetition_config ? (
                <>
                  <Typography variant="h6">
                    Every {data.repetition_config.interval_days}d
                    {data.repetition_config.interval_hours ? ` ${data.repetition_config.interval_hours}h` : ''}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {canPause && (
                      <Button
                        size="small"
                        startIcon={data.status === 'paused' ? <ResumeIcon /> : <PauseIcon />}
                        onClick={handlePauseResume}
                        variant="outlined"
                      >
                        {data.status === 'paused' ? 'Resume' : 'Pause'}
                      </Button>
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="h6" color="textSecondary">
                  Single Run
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      {pieData.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Execution Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {lineData.length > 1 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Success Rate Trend
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={lineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 100]} />
                    <ChartTooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="successRate"
                      stroke="#4caf50"
                      name="Success Rate (%)"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* Execution History Table */}
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Execution History
          </Typography>
          <IconButton onClick={loadExecutionHistory} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
        <Divider />

        {data.execution_history && data.execution_history.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Iteration</TableCell>
                  <TableCell>Scheduled For</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Files</TableCell>
                  <TableCell>Observatory</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.execution_history.map((exec) => (
                  <TableRow key={exec.execution_id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getExecutionStatusIcon(exec.status)}
                        {exec.iteration}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {formatUTC(exec.scheduled_for)}
                    </TableCell>
                    <TableCell>
                      {formatUTC(exec.started_at)}
                    </TableCell>
                    <TableCell>
                      {formatUTC(exec.completed_at)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={exec.status}
                        color={getExecutionStatusColor(exec.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {exec.files_captured || 0}
                    </TableCell>
                    <TableCell>
                      {exec.observatory_name || exec.observatory_id || '-'}
                    </TableCell>
                    <TableCell>
                      {exec.error_message || exec.skipped_reason || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box p={4} textAlign="center">
            <Typography color="textSecondary">
              No executions yet
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Current execution if any */}
      {data.current_execution && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Current Execution
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography color="textSecondary" variant="body2">Iteration</Typography>
              <Typography>{data.current_execution.iteration}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography color="textSecondary" variant="body2">Status</Typography>
              <Chip
                label={data.current_execution.status}
                color={getExecutionStatusColor(data.current_execution.status)}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography color="textSecondary" variant="body2">Assigned To</Typography>
              <Typography>{data.current_execution.assigned_to || '-'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography color="textSecondary" variant="body2">Started</Typography>
              <Typography>
                {data.current_execution.started_at
                  ? formatDistanceToNow(parseISO(data.current_execution.started_at), { addSuffix: true })
                  : '-'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );

  // If used as a dialog
  if (onClose) {
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Execution History - {data.name}</Typography>
            <IconButton onClick={onClose} size="small">
              <CancelIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {content}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // If used as standalone component
  return <Box>{content}</Box>;
};

export default ExecutionHistory;